import { StyleSheet, css } from 'aphrodite';
import { type FC, type MouseEvent, useState } from 'react';
import { Button } from '../../../web/components-v2/Button';
import { Toggle } from '../../../web/components/Toggle';
import type { KeywordPin, PinTrigger } from '../pins';
import { IconEdit, IconTrash } from './icons';

/**
 * One auto-send keyword pin row in the right rail.
 *
 * Matches `/tmp/design-bundle/focused-view/project/RightColumn.jsx`
 * (`KeywordPinRow`). Clicking the row body opens the editor; the toggle
 * switch and delete button stop propagation so they don't also trigger edit.
 *
 * Delete is hidden when `pin.builtin` is true — built-in pins stay in the
 * list but can only be toggled on/off or edited. The left border color
 * drops to `--border-strong` (and the row fades) when the pin is disabled,
 * to distinguish "inactive" from "not yet set up".
 */
type KeywordPinRowProps = {
	pin: KeywordPin;
	onToggle: () => void;
	onDelete: () => void;
	onEdit: () => void;
};

const TRIGGER_LABELS: Record<PinTrigger, string> = {
	before_call: 'Before call',
	on_dial: 'On dial',
	starter: 'Starter',
};

export const KeywordPinRow: FC<KeywordPinRowProps> = ({ pin, onToggle, onDelete, onEdit }) => {
	const [hovered, setHovered] = useState(false);

	const handleEditClick = (e: MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		onEdit();
	};

	const handleDeleteClick = (e: MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		onDelete();
	};

	return (
		<div
			role='button'
			tabIndex={0}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			onClick={onEdit}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onEdit();
				}
			}}
			className={css(styles.row)}
			style={{
				borderLeftColor: pin.enabled ? pin.color : 'var(--border-strong)',
				opacity: pin.enabled ? 1 : 0.6,
			}}
		>
			<div className={css(styles.main)}>
				<div className={css(styles.label)}>
					<span className={css(styles.labelText)}>{pin.label}</span>
				</div>
				<div className={css(styles.meta)}>
					<span>&quot;{pin.keyword}&quot;</span>
					<span className={css(styles.sep)}>·</span>
					<span>{TRIGGER_LABELS[pin.trigger]}</span>
				</div>
			</div>

			<div className={css(styles.actions)} style={{ opacity: hovered ? 1 : 0 }}>
				<Button variant='ghost' size='icon' title='Edit' onClick={handleEditClick}>
					<IconEdit size={14} />
				</Button>
				{!pin.builtin && (
					<Button variant='ghost' size='icon' title='Delete' onClick={handleDeleteClick}>
						<IconTrash size={14} />
					</Button>
				)}
			</div>

			<div onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} className={css(styles.toggleWrap)}>
				<Toggle id={`pin-toggle-${pin.id}`} isOn={pin.enabled} onToggleCheckChanged={onToggle} />
			</div>
		</div>
	);
};

const styles = StyleSheet.create({
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		padding: '9px 10px 9px 12px',
		background: 'var(--bg-elev)',
		border: '1px solid var(--border)',
		borderLeft: '3px solid var(--border-strong)',
		borderRadius: 8,
		cursor: 'pointer',
		transition: 'background 120ms, opacity 160ms',
		textAlign: 'left',
		':hover': {
			background: 'var(--hover)',
		},
	},
	main: {
		flex: 1,
		minWidth: 0,
	},
	label: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		fontSize: 12.5,
		color: 'var(--ink)',
		fontWeight: 500,
	},
	labelText: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	meta: {
		fontSize: 10.5,
		color: 'var(--ink-muted)',
		marginTop: 2,
		fontFamily: 'var(--font-mono)',
		display: 'flex',
		gap: 6,
		alignItems: 'center',
	},
	sep: {
		color: 'var(--ink-faint)',
	},
	actions: {
		display: 'flex',
		alignItems: 'center',
		gap: 2,
		transition: 'opacity 120ms',
	},
	toggleWrap: {
		flexShrink: 0,
		display: 'inline-flex',
		alignItems: 'center',
	},
});
